import {
  IRequirementResponse,
  PayeeTaxDocumentSharePermission,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces";
import { getRequirementDefinitionTableCellProps } from "../../../../RequirementDefinitions/utils";
import { CustomRequirement, RequirementStatusViewProps } from "../../../types";
import { getTaxVerificationRequirementName } from "../../TaxVerification/configs/helpers/getTaxVerificationRequirementName";
import { toWSDateString } from "@wingspanhq/fe-component-library";
import { getClientsCellProps } from "../helpers/getClientsCellProps";
import { useModalShareTaxInformationWithPayers } from "../../../../../shared/components/ModalShareTaxInformationWithPayers/useModalShareTaxInformationWithPayers";

export function useGetTaxVerificationCustomRequirement(): (
  requirements: IRequirementResponse[]
) => CustomRequirement {
  const modalFormW9Consent = useModalShareTaxInformationWithPayers();

  return requirements => {
    const baseRequirement = requirements[0];

    const completedTaxVerificationRequirements = requirements.filter(
      requirement =>
        requirement.status === RequirementStatus.Complete ||
        requirement.dataSourceMetadata?.verificationStatus === "Verified"
    );

    const consentsSharedRequirements = requirements.filter(
      requirement =>
        (requirement.dataSourceMetadata?.payeeOwnedData
          ?.shareTaxDocument as PayeeTaxDocumentSharePermission) ===
        PayeeTaxDocumentSharePermission.Allow
    );

    const status =
      completedTaxVerificationRequirements.length === requirements.length
        ? RequirementStatus.Complete
        : RequirementStatus.Incomplete;

    const latestUpdatedRequirement = requirements.reduce((latest, current) => {
      return current.updatedAt > latest.updatedAt ? current : latest;
    });

    const typeCell = {
      ...getRequirementDefinitionTableCellProps({
        type: baseRequirement.requirementType
      }),
      secondaryText: `Updated ${toWSDateString(
        latestUpdatedRequirement.updatedAt,
        "input"
      )}`
    };

    return {
      payerId: baseRequirement.payerId,
      status,
      clientsCell: getClientsCellProps(requirements),
      nameCell: {
        text: getTaxVerificationRequirementName(baseRequirement)
      },
      typeCell,
      engagementsCell: {},
      statusCell: {
        ...getStatusCellProps(
          completedTaxVerificationRequirements.length,
          requirements.length
        ),
        secondaryText: `Shared with ${consentsSharedRequirements.length} of ${requirements.length} clients`
      },
      menuActions:
        consentsSharedRequirements.length !== requirements.length
          ? [
              {
                label: "Consent to share information",
                icon: "clipboard-check",
                onClick: () => modalFormW9Consent.open()
              }
            ]
          : []
    };
  };
}

const getStatusCellProps = (
  completedRequirementsCount: number,
  totalRequirementsCount: number
): RequirementStatusViewProps => {
  if (completedRequirementsCount === totalRequirementsCount) {
    return {
      pill: {
        text: "Complete",
        theme: "success",
        icon: true
      }
    };
  } else if (completedRequirementsCount === 0) {
    return {
      pill: {
        theme: "warning",
        text: "Action required",
        icon: true
      }
    };
  } else {
    return {
      pill: {
        theme: "warning",
        text: "Partially completed",
        icon: true
      }
    };
  }
};
